/**
 * Hash-based router.
 *
 * Hash rather than History API on purpose: it needs no server rewrite rules, so
 * a deep link works identically on Netlify, on `file://`, and inside a future
 * Capacitor shell where there is no server at all. `netlify.toml` does have an
 * SPA redirect, but relying on it would make the app undeployable anywhere that
 * lacks one.
 */

export interface Route {
  /** e.g. "/collection/:id" */
  pattern: string;
  render: (params: Record<string, string>, host: HTMLElement) => void | Promise<void>;
}

let routes: Route[] = [];
let fallback: Route["render"] | null = null;
let outlet: HTMLElement | null = null;
let currentPath = "";

function parseHash(): string {
  const raw = location.hash.replace(/^#/, "");
  return raw.startsWith("/") ? raw : `/${raw}`;
}

function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split("/").filter(Boolean);
  const a = path.split("/").filter(Boolean);
  if (p.length !== a.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    const actual = a[i]!;
    if (seg.startsWith(":")) params[seg.slice(1)] = decodeURIComponent(actual);
    else if (seg !== actual) return null;
  }
  return params;
}

export function navigate(path: string, replace = false): void {
  const hash = `#${path}`;
  if (replace) history.replaceState(null, "", hash);
  else location.hash = hash;
  if (replace) void resolve();
}

export function currentRoute(): string {
  return currentPath;
}

async function resolve(): Promise<void> {
  if (!outlet) return;
  const path = parseHash();
  currentPath = path;

  for (const route of routes) {
    const params = match(route.pattern, path);
    if (params) {
      try {
        await route.render(params, outlet);
      } catch (error) {
        renderError(outlet, error);
      }
      return;
    }
  }

  if (fallback) await fallback({}, outlet);
}

function renderError(host: HTMLElement, error: unknown): void {
  // Screens throw rather than each handling its own failure; this is the single
  // place that turns a thrown error into something the user can read.
  const message = error instanceof Error ? error.message : String(error);
  host.replaceChildren();
  const box = document.createElement("div");
  box.className = "screen error-box";
  const heading = document.createElement("h2");
  heading.textContent = "Something went wrong";
  const detail = document.createElement("pre");
  detail.textContent = message; // textContent: the message may quote user data
  box.append(heading, detail);
  host.append(box);
  console.error(error);
}

export function startRouter(config: {
  host: HTMLElement;
  routes: Route[];
  fallback?: Route["render"];
  initial?: string;
}): void {
  outlet = config.host;
  routes = config.routes;
  fallback = config.fallback ?? null;

  addEventListener("hashchange", () => void resolve());

  if (!location.hash && config.initial) navigate(config.initial, true);
  else void resolve();
}

/** Force a re-render of the current route, e.g. after signing in. */
export function refresh(): void {
  void resolve();
}
